import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Modal,
  Space,
  Spin,
  Typography,
} from 'antd';

import {
  changeSyntheticBondCurrency,
  createSyntheticBondSeries,
  createSyntheticBondBatch,
  fetchSyntheticBondBatchStatus,
  fetchSyntheticBondGenerationOptions,
  fetchSyntheticBondSeries,
  fetchSyntheticBondPreviewJobV2,
  fetchSyntheticBondReferenceCurves,
  isSyntheticBondBatchTerminal,
  previewSyntheticBondBatchV2,
  retrySyntheticBondBatch,
  selectSyntheticBondSourceAsset,
  selectSyntheticBondSeries,
  setActiveSyntheticBondBatch,
  SYNTHETIC_BOND_FAMILY_METADATA_FIELDS,
  SyntheticBondCandidate,
  SyntheticBondCurveTenorMonths,
  SyntheticBondFamilyMetadata,
  SyntheticBondGenerationDraft,
  SyntheticBondSeries,
  syntheticBondSourceNodeMonths,
  syntheticBondTenorLabel,
  syntheticBondFamilyKey,
  syntheticBondPreviewChecksum,
  syntheticBondPreviewId,
  syntheticBondReferenceCurvePresetKey,
  syntheticBondSourceMapsEqual,
  updateSyntheticBondDraft,
  updateSyntheticBondFamilyMetadata,
} from 'state';
import { StoreState } from '../../../state/storeState';
import { BondChartMode } from '../BondsPlayground/BondChart';
import BatchIssuerForm from './BatchIssuerForm';
import BatchPreview, { syntheticBondPreviewHasBlockers } from './BatchPreview';
import BatchProgress from './BatchProgress';
import CandidatePricePreview, {
  CandidatePricePreviewHandle,
  v2PreviewCandidates,
} from './CandidatePricePreview';
import SyntheticBondGenerationGuide from './ModelGuide';
import SeriesSelector from './SeriesSelector';
import SystemCurveSidecar from './SystemCurveSidecar';
import '../BondsPlayground/styles.css';
import './styles.css';

const copyDraft = (draft: SyntheticBondGenerationDraft) =>
  JSON.parse(JSON.stringify(draft)) as SyntheticBondGenerationDraft;

let clientRequestSequence = 0;

const nextClientRequestId = (scope: string) =>
  `${scope}-${Date.now()}-${++clientRequestSequence}`;

const SyntheticBondsV2: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const state = useSelector((store: StoreState) => store.app.synthetic_bonds);
  const [chartMode, setChartMode] = useState<BondChartMode>('yield');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const candidatePreviewRef = useRef<CandidatePricePreviewHandle>(null);
  const selectPreviewCandidate = useCallback(
    (candidate: SyntheticBondCandidate) =>
      candidatePreviewRef.current?.selectCandidate(candidate),
    [],
  );

  const {
    activeBatch,
    activeBatchId,
    activePreviewV2JobId,
    candidatePreviewsV2ByJobId,
    createError,
    createSubmitting,
    draftRevision,
    draftSpec,
    options,
    optionsError,
    optionsFetching,
    series: bondSeries,
    seriesError,
    seriesFetching,
    seriesSaveError,
    seriesSaving,
    previewV2: preview,
    previewV2Error: previewError,
    previewingV2: previewing,
    previewV2JobsById,
    previewV2StatusFetching,
    referenceCurvePresetsByCurrencyKey,
    referenceCurvesByFamilyKey,
    retryError,
    retrying,
    statusError,
    statusFetching,
    selectedReferenceCurvePresetId,
  } = state;

  const familyKey = syntheticBondFamilyKey(
    draftSpec.currency_id,
    draftSpec.source_asset_ids_by_tenor_months,
  );
  const referenceEntry = referenceCurvesByFamilyKey[familyKey];
  const referencePresetEntry = draftSpec.currency_id
    ? referenceCurvePresetsByCurrencyKey[
        syntheticBondReferenceCurvePresetKey(draftSpec.currency_id)
      ]
    : undefined;
  const selectedReferencePreset = referencePresetEntry?.data?.results.find(
    preset => preset.id === selectedReferenceCurvePresetId,
  );
  const selectedReferencePresetModified = Boolean(
    selectedReferencePreset &&
      !syntheticBondSourceMapsEqual(
        draftSpec.source_asset_ids_by_tenor_months,
        selectedReferencePreset.source_asset_ids_by_tenor_months,
      ),
  );
  const previewId = syntheticBondPreviewId(preview);
  const previewChecksum = syntheticBondPreviewChecksum(preview);
  const activePreviewJob = activePreviewV2JobId
    ? previewV2JobsById[activePreviewV2JobId]
    : undefined;
  const candidatePreviews = activePreviewV2JobId
    ? candidatePreviewsV2ByJobId[activePreviewV2JobId] || {}
    : {};
  const previewCandidateItems = useMemo(
    () => v2PreviewCandidates(preview),
    [preview],
  );
  const allCandidateCurvesPreloaded = Boolean(
    previewCandidateItems.length &&
      previewCandidateItems.every(candidate => {
        const id =
          candidate.batch_item_id || candidate.id || candidate.uuid || '';
        return Boolean(id && candidatePreviews[id]);
      }),
  );
  const candidateCount = Number(
    preview?.summary?.candidate_total ||
      preview?.asset_candidate_count ||
      preview?.candidate_count ||
      preview?.items?.length ||
      preview?.candidates?.length ||
      0,
  );
  const expectedRows = Number(
    preview?.summary?.projected_market_history_total ||
      preview?.expected_market_history_rows ||
      preview?.projected_market_history_rows ||
      0,
  );
  const existingCount = Number(
    preview?.summary?.existing_total || preview?.existing_count || 0,
  );
  const batchBusy =
    Boolean(activeBatchId) &&
    (!activeBatch || !isSyntheticBondBatchTerminal(activeBatch.status));

  useEffect(() => {
    if (!options && !optionsFetching && !optionsError) {
      dispatch(fetchSyntheticBondGenerationOptions({}));
    }
  }, [dispatch, options, optionsError, optionsFetching]);

  useEffect(() => {
    if (!bondSeries && !seriesFetching && !seriesError) {
      dispatch(fetchSyntheticBondSeries({}));
    }
  }, [bondSeries, dispatch, seriesError, seriesFetching]);

  useEffect(() => {
    if (
      !draftSpec.currency_id ||
      referenceEntry?.fetching ||
      referenceEntry?.fetched ||
      referenceEntry?.error
    ) {
      return;
    }
    dispatch(
      fetchSyntheticBondReferenceCurves({
        currency_id: draftSpec.currency_id,
        source_asset_ids_by_tenor_months: {
          ...draftSpec.source_asset_ids_by_tenor_months,
        },
      }),
    );
  }, [
    dispatch,
    draftSpec.currency_id,
    draftSpec.source_asset_ids_by_tenor_months,
    familyKey,
    referenceEntry?.error,
    referenceEntry?.fetched,
    referenceEntry?.fetching,
  ]);

  useEffect(() => {
    const routeBatch = new URLSearchParams(location.search).get('batch');
    if (routeBatch && routeBatch !== activeBatchId) {
      dispatch(setActiveSyntheticBondBatch({ batch_id: routeBatch }));
    }
  }, [activeBatchId, dispatch, location.search]);

  useEffect(() => {
    if (!activeBatchId) return;
    const params = new URLSearchParams(location.search);
    if (params.get('batch') === activeBatchId) return;
    params.set('batch', activeBatchId);
    history.replace({ ...location, search: `?${params.toString()}` });
  }, [activeBatchId, history, location]);

  useEffect(() => {
    if (!activeBatchId || activeBatch || statusFetching || statusError) return;
    dispatch(
      fetchSyntheticBondBatchStatus({
        batch_id: activeBatchId,
        _client_request_id: nextClientRequestId('status'),
      }),
    );
  }, [activeBatch, activeBatchId, dispatch, statusError, statusFetching]);

  useEffect(() => {
    if (
      !activeBatchId ||
      statusFetching ||
      isSyntheticBondBatchTerminal(activeBatch?.status)
    ) {
      return;
    }
    const timeout = window.setTimeout(
      () =>
        dispatch(
          fetchSyntheticBondBatchStatus({
            batch_id: activeBatchId,
            _client_request_id: nextClientRequestId('status'),
          }),
        ),
      statusError ? 5000 : 2500,
    );
    return () => window.clearTimeout(timeout);
  }, [
    activeBatch?.status,
    activeBatchId,
    dispatch,
    statusError,
    statusFetching,
  ]);

  useEffect(() => {
    if (
      !activePreviewV2JobId ||
      !activePreviewJob ||
      previewV2StatusFetching ||
      activePreviewJob.status === 'FAILED' ||
      (activePreviewJob.status === 'SUCCEEDED' &&
        preview &&
        allCandidateCurvesPreloaded)
    ) {
      return;
    }
    const delay = activePreviewJob.status === 'SUCCEEDED' ? 0 : 2500;
    const timeout = window.setTimeout(
      () =>
        dispatch(
          fetchSyntheticBondPreviewJobV2({
            preview_job_id: activePreviewV2JobId,
          }),
        ),
      delay,
    );
    return () => window.clearTimeout(timeout);
  }, [
    activePreviewJob,
    activePreviewV2JobId,
    allCandidateCurvesPreloaded,
    dispatch,
    preview,
    previewV2StatusFetching,
  ]);

  const handleDraftChange = (patch: Partial<SyntheticBondGenerationDraft>) =>
    dispatch(updateSyntheticBondDraft(patch));
  const handleMetadataChange = (
    field: keyof SyntheticBondFamilyMetadata,
    value: string | number | null,
  ) => dispatch(updateSyntheticBondFamilyMetadata({ field, value }));
  const handleCurrencyChange = (currencyId: number | null) =>
    dispatch(changeSyntheticBondCurrency({ currency_id: currencyId }));
  const handleSourceChange = (
    tenorMonths: SyntheticBondCurveTenorMonths,
    assetId: number | null,
  ) =>
    dispatch(
      selectSyntheticBondSourceAsset({
        tenor_months: tenorMonths,
        asset_id: assetId,
      }),
    );
  const handleSeriesSelect = (series?: SyntheticBondSeries) =>
    dispatch(selectSyntheticBondSeries({ series }));
  const canSaveSeries = Boolean(
    !draftSpec.series_id &&
      draftSpec.currency_id &&
      selectedReferencePreset &&
      !selectedReferencePresetModified &&
      draftSpec.initial_tenors.length === 1 &&
      /^[A-Za-z]{2}$/.test(draftSpec.family_metadata.country_code) &&
      draftSpec.family_metadata.geography.trim() &&
      draftSpec.family_metadata.region !== null &&
      draftSpec.family_metadata.market_hours_id !== null &&
      Object.values(draftSpec.source_asset_ids_by_tenor_months).every(Boolean),
  );
  const handleSaveSeries = () => {
    if (
      !canSaveSeries ||
      !draftSpec.currency_id ||
      !selectedReferenceCurvePresetId
    ) {
      return;
    }
    const {
      country_code,
      issuer_type: _issuerType,
      ...familyMetadataDefaults
    } = draftSpec.family_metadata;
    void _issuerType;
    const initialTenor = draftSpec.initial_tenors[0];
    dispatch(
      createSyntheticBondSeries({
        country_code,
        initial_tenor_years: initialTenor,
        currency_id: draftSpec.currency_id,
        reference_curve_preset_id: selectedReferenceCurvePresetId,
        family_label:
          draftSpec.family_label ||
          `${country_code.toUpperCase()}${initialTenor}Y`,
        family_metadata_defaults: familyMetadataDefaults,
        is_active: true,
      }),
    );
  };
  const fetchReferenceCurves = () => {
    if (!draftSpec.currency_id) return;
    dispatch(
      fetchSyntheticBondReferenceCurves({
        currency_id: draftSpec.currency_id,
        source_asset_ids_by_tenor_months: {
          ...draftSpec.source_asset_ids_by_tenor_months,
        },
      }),
    );
  };
  const handlePreview = () =>
    dispatch(
      previewSyntheticBondBatchV2({
        ...copyDraft(draftSpec),
        _client_request_id: nextClientRequestId('preview-v2'),
        _client_draft_revision: draftRevision,
      }),
    );
  const handleCreate = () => {
    if (!previewId || !previewChecksum) return;
    setConfirmationOpen(false);
    dispatch(
      createSyntheticBondBatch({
        batch_id: previewId,
        preview_checksum: previewChecksum,
      }),
    );
  };
  const refreshStatus = () =>
    activeBatchId &&
    dispatch(
      fetchSyntheticBondBatchStatus({
        batch_id: activeBatchId,
        _client_request_id: nextClientRequestId('status'),
      }),
    );
  const retryBatch = () =>
    activeBatchId &&
    dispatch(
      retrySyntheticBondBatch({
        batch_id: activeBatchId,
        _client_request_id: nextClientRequestId('retry'),
      }),
    );

  const selectedCurrency =
    preview?.family?.currency ||
    options?.currencies.find(currency => currency.id === draftSpec.currency_id);
  const selectedSources = useMemo(
    () =>
      preview?.family?.source_nodes?.length
        ? preview.family.source_nodes.map(
            node =>
              `${syntheticBondTenorLabel(
                syntheticBondSourceNodeMonths(node),
              )} ${node.name}`,
          )
        : (referenceEntry?.data?.tenors || [])
            .filter(slot => slot.asset)
            .map(slot => `${slot.tenor_label} ${slot.asset?.name}`),
    [preview?.family?.source_nodes, referenceEntry?.data?.tenors],
  );
  const reviewedMetadata =
    preview?.family?.family_metadata || draftSpec.family_metadata;
  const familyRevision =
    preview?.family?.id || preview?.family_revision_id || '—';
  const familyHash =
    preview?.family?.definition_hash ||
    preview?.family_definition_hash ||
    preview?.family_hash ||
    '—';
  const reviewedVersions = preview?.versions;
  const createDisabled =
    !previewId ||
    !previewChecksum ||
    syntheticBondPreviewHasBlockers(preview) ||
    !allCandidateCurvesPreloaded ||
    previewing ||
    createSubmitting ||
    batchBusy;

  return (
    <div className="synthetic-bonds">
      <main className="synthetic-bonds__workspace">
        <Card title="Synthetic Bond Generation · V2">
          <SyntheticBondGenerationGuide />

          <Divider />

          {optionsFetching && !options ? (
            <div className="synthetic-bonds__page-loading">
              <Spin />
              <Typography.Text type="secondary">
                Loading generation capabilities…
              </Typography.Text>
            </div>
          ) : optionsError && !options ? (
            <Alert
              action={
                <Button
                  size="small"
                  onClick={() =>
                    dispatch(fetchSyntheticBondGenerationOptions({}))
                  }
                >
                  Retry
                </Button>
              }
              description={optionsError}
              message="Generation options could not be loaded"
              showIcon
              type="error"
            />
          ) : (
            <>
              <SeriesSelector
                canSave={canSaveSeries}
                disabled={batchBusy || createSubmitting}
                draft={draftSpec}
                error={seriesError}
                fetching={seriesFetching}
                onSave={handleSaveSeries}
                onSelect={handleSeriesSelect}
                saveError={seriesSaveError}
                saving={seriesSaving}
                series={bondSeries}
              />
              <Divider />
              <BatchIssuerForm
                disabled={batchBusy || createSubmitting}
                draft={draftSpec}
                onCurrencyChange={handleCurrencyChange}
                onDraftChange={handleDraftChange}
                onMetadataChange={handleMetadataChange}
                onPreview={handlePreview}
                options={options}
                previewing={previewing}
                seriesLocked={Boolean(draftSpec.series_id)}
              />
            </>
          )}

          {previewError && (
            <Alert
              className="synthetic-bonds__request-error"
              description={previewError}
              message="Preview could not be completed"
              showIcon
              type="error"
            />
          )}

          {previewing && !previewError && (
            <Alert
              className="synthetic-bonds__request-error"
              description="The request returned immediately. Celery is calculating every candidate curve and the admin socket will deliver them here when ready."
              message="Candidate preview is running in the background"
              showIcon
              type="info"
            />
          )}

          {preview && (
            <>
              <BatchPreview
                draft={draftSpec}
                onSelectCandidate={selectPreviewCandidate}
                preview={preview}
                referenceCurves={referenceEntry?.data}
              />

              <CandidatePricePreview
                ref={candidatePreviewRef}
                candidatePreviews={candidatePreviews}
                preloadComplete={allCandidateCurvesPreloaded}
                preview={preview}
              />

              {createError && (
                <Alert
                  className="synthetic-bonds__request-error"
                  description={createError}
                  message="Creation could not be queued"
                  showIcon
                  type="error"
                />
              )}

              <div className="synthetic-bonds__create-action">
                <div>
                  <Typography.Text strong>
                    Ready to publish the reviewed batch?
                  </Typography.Text>
                  <Typography.Paragraph type="secondary">
                    Create sends only the immutable batch ID and checksum.
                  </Typography.Paragraph>
                </div>
                <Button
                  danger
                  disabled={createDisabled}
                  loading={createSubmitting}
                  onClick={() => setConfirmationOpen(true)}
                  size="large"
                  type="primary"
                >
                  Create {candidateCount} AFS bonds
                </Button>
              </div>
            </>
          )}

          {activeBatch && (
            <BatchProgress
              batch={activeBatch}
              error={statusError}
              fetching={statusFetching}
              onRefresh={refreshStatus}
              onRetry={retryBatch}
              retryError={retryError}
              retrying={retrying}
            />
          )}
        </Card>
      </main>

      <SystemCurveSidecar
        data={referenceEntry?.data}
        disabled={batchBusy || createSubmitting || Boolean(draftSpec.series_id)}
        draft={draftSpec}
        error={referenceEntry?.error}
        fetching={referenceEntry?.fetching}
        mode={chartMode}
        onCurrencyChange={handleCurrencyChange}
        onModeChange={setChartMode}
        onRetry={fetchReferenceCurves}
        onSelectSource={handleSourceChange}
        options={options}
      />

      <Modal
        cancelText="Keep reviewing"
        confirmLoading={createSubmitting}
        okButtonProps={{ danger: true }}
        okText={`Create ${candidateCount} AFS bonds`}
        open={confirmationOpen}
        title="Create reviewed synthetic bond assets?"
        width={680}
        onCancel={() => setConfirmationOpen(false)}
        onOk={handleCreate}
      >
        <Alert
          description="This writes real AFSBond assets and clean bid/ask MarketHistory rows. Existing published rows will not be overwritten."
          message="Persistent financial-data operation"
          showIcon
          type="warning"
        />
        <Descriptions
          bordered
          className="synthetic-bonds__confirmation-details"
          column={1}
          size="small"
        >
          <Descriptions.Item label="Currency / family">
            {selectedCurrency?.symbol || draftSpec.currency_id} ·{' '}
            {draftSpec.family_label}
          </Descriptions.Item>
          <Descriptions.Item label="Target class">
            {draftSpec.target_kind}
          </Descriptions.Item>
          <Descriptions.Item label="Family revision / hash">
            <Typography.Text copyable>
              {String(familyRevision)} · {String(familyHash)}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Source nodes">
            {selectedSources.join(' · ')}
          </Descriptions.Item>
          <Descriptions.Item label="Reference curve family">
            {selectedReferencePreset
              ? `${selectedReferencePreset.code} · ${
                  selectedReferencePreset.label
                }${selectedReferencePresetModified ? ' (modified)' : ''}`
              : 'Custom source assignments'}
          </Descriptions.Item>
          <Descriptions.Item label="Uniform family metadata">
            <Space size={[4, 4]} wrap>
              {SYNTHETIC_BOND_FAMILY_METADATA_FIELDS.map(
                field => [field, reviewedMetadata[field]] as const,
              )
                .filter(([, value]) => value !== null && String(value) !== '')
                .map(([field, value]) => (
                  <Typography.Text code key={field}>
                    {field}: {String(value)}
                  </Typography.Text>
                ))}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Issuance program">
            {draftSpec.initial_tenors.join(', ')}Y ·{' '}
            {draftSpec.first_issue_date} → {draftSpec.last_issue_date}
          </Descriptions.Item>
          <Descriptions.Item label="Output">
            {candidateCount.toLocaleString()} assets ·{' '}
            {expectedRows.toLocaleString()} expected MarketHistory rows
          </Descriptions.Item>
          <Descriptions.Item label="Already identical / no-op">
            {existingCount.toLocaleString()} assets
          </Descriptions.Item>
          <Descriptions.Item label="Reviewed identity">
            Batch {previewId} · checksum {previewChecksum?.slice(0, 16)}…
          </Descriptions.Item>
          <Descriptions.Item label="Versions">
            publication{' '}
            {reviewedVersions?.publication ||
              options?.publication_version ||
              '—'}{' '}
            · family{' '}
            {reviewedVersions?.family_definition_schema ||
              options?.family_definition_schema_version ||
              '—'}{' '}
            · pricing{' '}
            {reviewedVersions?.pricing || options?.pricing_version || '—'} ·
            identifier{' '}
            {reviewedVersions?.identifier_scheme ||
              options?.identifier_scheme_version ||
              '—'}
          </Descriptions.Item>
        </Descriptions>
        <Typography.Paragraph type="secondary">
          Successful generation does not add these assets to a session. They
          must be selected through the normal historical-session setup.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
};

export default SyntheticBondsV2;
